/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';

export const uploadSchema = Yup.object().shape({
  uid: Yup.string().required('UID is required'),
  uploader: Yup.string().nullable(),
  hasAccessUsers: Yup.array().of(Yup.string()).required('Access users are required'),
  fileID: Yup.string().required('File ID is required'),
  filename: Yup.string().required('Filename is required'),
  material: Yup.string().required('Material is required'),
  cappingStyle: Yup.string().required('Capping style is required'),
  cappingOptions: Yup.string().required('Capping options are required'),
  quantity: Yup.number().required('Quantity is required').min(1, 'Quantity must be at least 1'),
  optionalNotes: Yup.string().nullable(),
  hasBeenOrdered: Yup.boolean().default(false).required('Has been ordered flag is required'),
  isParallelShared: Yup.boolean().default(false), // optional field to indicate if the upload is a Parallel Part that will share with all customers
  uploadedAt: Yup.number().default(Date.now()).required('Uploaded at timestamp is required'),
  expiresAt: Yup.number()
    .default(() => Date.now() + 3 * 30 * 24 * 60 * 60 * 1000) // 3 months
    .required('Expires at timestamp is required'),
  email: Yup.string().nullable().email('Email must be a valid email address'),
  quote: Yup.string().nullable(),
});

/**
 * @typedef {Yup.InferType<typeof uploadSchema>} UploadSchema
 * @typedef {BaseDocData & UploadSchema} UploadDocData
 */

/**
 * Class representing a basic uploaded part.
 * @class
 */
export default class Upload {
  /** @type {UploadDocData['validation']} */ validation = 'passed';
  /** Firestore document ID @type {UploadDocData['id']} */ id;

  /** @type {UploadDocData['uid']} */ uid;
  /** @type {UploadDocData['uploader']} */ uploader;
  /** @type {UploadDocData['hasAccessUsers']} */ hasAccessUsers;

  /** @type {UploadDocData['fileID']} */ fileID;
  /** @type {UploadDocData['filename']} */ filename;

  /** @type {UploadDocData['material']} */ material;
  /** @type {UploadDocData['cappingStyle']} */ cappingStyle;
  /** @type {UploadDocData['cappingOptions']} */ cappingOptions;

  /** @type {UploadDocData['quantity']} */ quantity;
  /** @type {UploadDocData['optionalNotes']} */ optionalNotes;

  /** @type {UploadDocData['hasBeenOrdered']} */ hasBeenOrdered;
  /** @type {UploadDocData['isParallelShared']} */ isParallelShared;
  /** @type {UploadDocData['uploadedAt']} */ uploadedAt;
  /** @type {UploadDocData['expiresAt']} */ expiresAt;

  /** @type {UploadDocData['email']} */ email;
  /** @type {UploadDocData['quote']} */ quote;

  /**
   * Create an {@link Upload}.
   * @param {UploadDocData} param0 - An {@link UploadDocData} object.
   */
  constructor({
    id,
    uid,
    uploader,
    hasAccessUsers,
    fileID,
    filename,
    material,
    cappingStyle,
    cappingOptions,
    quantity,
    optionalNotes,
    hasBeenOrdered,
    isParallelShared,
    uploadedAt,
    expiresAt,
    email,
    quote,
  }) {
    const validatedData = uploadSchema.validateSync({
      uid,
      uploader,
      hasAccessUsers,
      fileID,
      filename,
      material,
      cappingStyle,
      cappingOptions,
      quantity,
      optionalNotes,
      hasBeenOrdered,
      isParallelShared,
      uploadedAt,
      expiresAt,
      email,
      quote,
    });
    this.id = id || null;
    this.uid = validatedData.uid;
    this.uploader = validatedData.uploader || uid;
    this.hasAccessUsers = validatedData.hasAccessUsers || [uid];
    this.fileID = validatedData.fileID;
    this.filename = validatedData.filename;
    this.material = validatedData.material;
    this.cappingStyle = validatedData.cappingStyle;
    this.cappingOptions = validatedData.cappingOptions || null;
    this.quantity = validatedData.quantity;
    this.optionalNotes = validatedData.optionalNotes || null;
    this.hasBeenOrdered = validatedData.hasBeenOrdered;
    this.isParallelShared = validatedData.isParallelShared;
    this.uploadedAt = validatedData.uploadedAt;
    this.expiresAt = validatedData.expiresAt;
    this.email = validatedData.email;
    this.quote = validatedData.quote;
  }

  /**
   * Converts the {@link Upload} instance to a Firestore data object.
   * @returns {Omit<UploadDocData, 'id'>} {@link UploadDocData} (without `id`).
   */
  toFirestore() {
    return {
      uid: this.uid,
      uploader: this.uploader,
      hasAccessUsers: this.hasAccessUsers,
      fileID: this.fileID,
      filename: this.filename,
      material: this.material,
      cappingStyle: this.cappingStyle,
      cappingOptions: this.cappingOptions,
      quantity: this.quantity,
      optionalNotes: this.optionalNotes,
      hasBeenOrdered: this.hasBeenOrdered,
      isParallelShared: this.isParallelShared,
      uploadedAt: this.uploadedAt,
      expiresAt: this.expiresAt,
      email: this.email,
      quote: this.quote,
    };
  }

  /**
   * Creates an {@link Upload} instance from Firestore data.
   * @param {any} docData - Raw Firestore document.
   * @returns {Upload} An {@link Upload} instance.
   */
  static fromFirestore(docData) {
    return new Upload(docData);
  }
}
